import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {useSelector} from '../../reduxHooks';
import { Button } from '@betareader/design-system';
import styled from 'styled-components';
import Icon from '../AppIcon';
import {
  CritiquePost,
  useCreatePost,
  useCreateReply,
  useCritiquePostReplies,
  useCritiquePosts,
  usePinPost,
} from 'src/hooks/useCritiqueGroups';
import { useFeatureFlags } from 'src/hooks/useFeatureFlags';
import { featureToggleUtil } from 'src/utils';
import { userRefName } from 'src/utils/critiqueGroupUtil';
import { CritiqueAvatar } from './critiqueAvatar';
import { CritiqueEmptyState, CritiqueErrorState, CritiqueLoadingState } from './critiqueStates';

// Client-side cap for the topic line. Mirrors the API's TITLE_MAX_LENGTH
// (main-apps/api/src/models/groups/groupPost.js) — the server is the real
// authority (400 TITLE_TOO_LONG); this just stops typing past it. The two apps
// don't share code, so keep this value in sync if the server cap changes.
const TOPIC_MAX_LENGTH = 140;

/**
 * Is the topic-threads presentation on for THIS user?
 *
 * Two-tier, mirroring the server's `isFeatureEnabledForUser` (global flag OR
 * `user.betaFeatures[key]`) — and matching how `critique_groups` itself is
 * gated in `containers/critiqueGroups/index.tsx`.
 *
 * Checking only the global flag would desync the two halves of this feature:
 * `wall/listPosts.js` resolves the SAME flag per-user, so a beta-granted user
 * would get the latest-activity ordering from the server while this component
 * still rendered the flat wall — threads silently reordering on reply, which is
 * exactly the "no change until toggled" invariant the flag exists to give.
 */
export const useCritiqueTopicsEnabled = (): boolean => {
  const { flags } = useFeatureFlags();
  const userProfile = useSelector((state: any) => state.user.userProfile);
  return (
    flags?.critique_group_topics === true ||
    featureToggleUtil.hasFeatureAccess(userProfile, 'critique_group_topics')
  );
};

/**
 * The group discussion wall (design E2 / I3) — posts with threaded replies and
 * host-pinned prompts. The server orders pinned-first then newest-first and
 * silently ignores `pinned` from a non-moderator, so the pin control is only
 * rendered for those who actually have it.
 */

const Composer = styled.div`
  padding: 16px 20px;
  background: var(--br-surface);
  border: 1px solid var(--br-border);
  border-radius: 13px;
  display: flex;
  gap: 12px;
  /* Clear the pinned-prompt list below rather than sitting on top of it. */
  margin-bottom: 24px;

  /*
   * The app ships a global, bare-selector \`textarea { height: 100% }\`. The form
   * beside the avatar is a stretched flex item, so that 100% resolves to the
   * whole card: the textarea eats it, \`rows={2}\` is ignored, and the pin row
   * below is pushed clean OUT of the card and onto the pinned-prompt list —
   * which is why no amount of margin on that row could separate the two.
   * One class of specificity is enough to win it back; scoped here so the
   * global rule keeps applying everywhere else that relies on it.
   */
  textarea {
    height: auto;
  }
`;

/**
 * The pin + Post row under the textarea.
 *
 * Inset a full 20px — the composer's own horizontal padding — so the checkbox
 * reads as part of the card instead of a strip jammed against its edges, and
 * the Post button is pulled the same distance off the right. An earlier 12px
 * was too subtle to register.
 */
const ComposerActions = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  flex-wrap: wrap;
  padding: 14px 20px;
  margin-top: 12px;
`;

const ComposerTextArea = styled.textarea`
  box-sizing: border-box;
  display: block;
  width: 100%;
  height: auto;
  min-height: 72px;
  padding: 10px 12px;
  border: 1px solid var(--br-border);
  border-radius: var(--br-control-radius);
  background: var(--br-surface);
  color: var(--br-text);
  font: inherit;
  resize: vertical;
  &:focus-visible { outline: 2px solid var(--br-focus); outline-offset: 2px; }
`;

const PostCard = styled.div<{ $pinned: boolean }>`
  background: var(--br-surface);
  border: 1px solid ${({ $pinned }) => ($pinned ? 'var(--br-warning-border)' : 'var(--br-border)')};
  border-radius: 13px;
  overflow: hidden;
  margin-bottom: 16px;
`;

const PinnedFlag = styled.div`
  background: var(--br-raised-surface);
  padding: 7px 14px;
  font-size: 11px;
  font-weight: 800;
  color: var(--br-warning-text);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  display: flex;
  align-items: center;
  gap: 6px;
`;

const PostBody = styled.div`
  padding: 14px 16px;
`;

const PostText = styled.p`
  margin: 10px 0 0;
  font-size: 14px;
  line-height: 1.55;
  color: var(--br-text);
  white-space: pre-wrap;
  word-break: break-word;
`;

const MetaRow = styled.div`
  display: flex;
  gap: 18px;
  margin-top: 12px;
  font-size: 12.5px;
  color: var(--br-muted);
  align-items: center;
`;

const MetaButton = styled.button`
  background: none;
  border: none;
  padding: 0;
  font: inherit;
  color: var(--br-muted);
  cursor: pointer;
  &:hover {
    color: var(--br-accent);
  }
`;

const Reply = styled.div`
  padding: 12px 16px 14px 40px;
  border-top: 1px solid var(--br-border);
  background: var(--br-raised-surface);
`;

/* ── Topic-thread presentation (critique_group_topics flag) ─────────────────── */

// The thread header row: a clickable toggle region plus (for hosts) a pin
// control as a SIBLING — never nested — so no interactive element sits inside
// another and the heading's <h4> isn't placed inside a <button>.
const ThreadHeader = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 4px;
  padding: 8px 12px 8px 8px;
`;

// The clickable open/close region. A div (role="button") rather than a real
// <button>, because it contains an <h4> topic heading — invalid as button
// content — and so the pin <button> can sit beside it without nesting.
const ThreadToggle = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  gap: 12px;
  align-items: flex-start;
  padding: 6px 8px;
  border-radius: 10px;
  cursor: pointer;
  &:hover {
    background: var(--br-raised-surface);
  }
  &:focus-visible {
    outline: 2px solid var(--br-focus);
    outline-offset: 2px;
  }
`;

const TopicTitle = styled.h4`
  margin: 0 0 3px;
  font-size: 15px;
  font-weight: 800;
  color: var(--br-text);
  word-break: break-word;
`;

const TopicMeta = styled.div`
  font-size: 12px;
  color: var(--br-muted);
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  align-items: center;
`;

// A plain input (not semantic's <Input>, whose wrapper div would swallow the
// data-testid and value setter) for the optional topic line.
const TopicInput = styled.input`
  width: 100%;
  box-sizing: border-box;
  padding: 9px 12px;
  margin-bottom: 8px;
  border: 1px solid var(--br-border);
  border-radius: 8px;
  font-size: 14px;
  font-weight: 700;
  color: var(--br-text);
  &::placeholder {
    font-weight: 400;
    color: var(--br-muted);
  }
  &:focus {
    outline: none;
    border-color: var(--br-focus);
  }
`;

const ReplyText = styled.p`
  margin: 6px 0 0;
  font-size: 13px;
  color: var(--br-text);
  padding-left: 35px;
  white-space: pre-wrap;
  word-break: break-word;
`;

const relativeTime = (iso?: string, t?: any): string => {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  const mins = Math.round((Date.now() - then) / 60000);
  if (mins < 1) return t ? t('JustNow', 'just now') : 'just now';
  if (mins < 60) return `${mins}m`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.round(hours / 24);
  if (days === 1) return t ? t('Yesterday', 'yesterday') : 'yesterday';
  if (days < 7) return `${days}d`;
  return new Date(iso).toLocaleDateString();
};

// Fallback heading for a titleless post (a quick post, or a legacy one from
// before topics existed): a short one-line snippet of the body.
const textSnippet = (text?: string): string => {
  const clean = (text || '').replace(/\s+/g, ' ').trim();
  if (!clean) return '…';
  // Slice by code point (Array.from), not UTF-16 unit, so an emoji or other
  // astral character at the boundary isn't split into a stray � glyph.
  const chars = Array.from(clean);
  return chars.length > 80 ? `${chars.slice(0, 80).join('')}…` : clean;
};

const PostThread: React.FC<{
  groupId: string;
  post: CritiquePost;
  canPin: boolean;
  /** Topic-thread presentation (critique_group_topics flag). */
  topicsEnabled: boolean;
}> = ({ groupId, post, canPin, topicsEnabled }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');
  const replies = useCritiquePostReplies(groupId, post._id, { enabled: open } as any);
  const createReply = useCreateReply(groupId);
  const pinPost = usePinPost(groupId);

  const submit = () => {
    const trimmed = text.trim();
    if (!trimmed) return;
    createReply.mutate(
      { postId: post._id, text: trimmed },
      { onSuccess: () => setText('') }
    );
  };

  const replyCountLabel =
    post.replyCount === 1
      ? t('CritiqueOneReply', '1 reply')
      : t('CritiqueNReplies', '{{count}} replies', {
          count: post.replyCount ?? 0,
        });

  // Shared: the pin control. `stopPropagation` matters only in the topic layout
  // (where the header is itself a toggle button); harmless in the flat layout.
  const pinControl = canPin && (
    <MetaButton
      onClick={e => {
        e.stopPropagation();
        pinPost.mutate({ postId: post._id, pinned: !post.pinned });
      }}
      title={post.pinned ? t('Unpin', 'Unpin') : t('Pin', 'Pin')}
      data-testid="wall-pin-toggle"
    >
      <Icon
        name="thumbtack"
        style={{ color: post.pinned ? 'var(--br-warning-text)' : 'var(--br-muted)' }}
        aria-hidden="true"
      />
    </MetaButton>
  );

  // Shared: the replies list + reply composer (identical in both layouts).
  const repliesSection = (
    <>
      {replies.isLoading && (
        <Reply>
          <Icon name="spinner" loading /> {t('Loading', 'Loading…')}
        </Reply>
      )}
      {(replies.data || []).map(r => (
        <Reply key={r._id} data-testid="wall-reply">
          <div style={{ display: 'flex', gap: 9, alignItems: 'center' }}>
            <CritiqueAvatar
              id={r.createdBy?._id || ''}
              name={r.createdBy?.displayName}
              size={26}
            />
            <span style={{ fontWeight: 700, fontSize: 13 }}>
              {userRefName(r.createdBy, t)}
            </span>
            <span style={{ fontSize: 11, color: '#a49f93' }}>
              · {relativeTime(r.createdAt, t)}
            </span>
          </div>
          <ReplyText>{r.text}</ReplyText>
        </Reply>
      ))}
      <Reply>
        <form onSubmit={event => { event.preventDefault(); submit(); }}>
          <ComposerTextArea
            rows={2}
            value={text}
            placeholder={t('CritiqueWriteReply', 'Write a reply…')}
            aria-label={t('CritiqueWriteReply', 'Write a reply…')}
            onChange={event => setText(event.target.value)}
            data-testid="wall-reply-input"
          />
          <Button
            type="submit"
            size="small"
            aria-busy={createReply.isPending}
            disabled={!text.trim() || createReply.isPending}
            style={{ marginTop: 8, borderRadius: 7 }}
            data-testid="wall-reply-submit"
          >
            {t('Reply', 'Reply')}
          </Button>
        </form>
      </Reply>
    </>
  );

  // ── Topic-thread layout: a collapsed row (topic + meta) that opens to the
  // post body and its replies. ────────────────────────────────────────────────
  if (topicsEnabled) {
    const heading = (post.title && post.title.trim()) || textSnippet(post.text);
    return (
      <PostCard $pinned={post.pinned} data-testid={`wall-post-${post._id}`}>
        {post.pinned && (
          <PinnedFlag data-testid="wall-post-pinned">
            <Icon name="thumbtack" aria-hidden="true" />
            {t('CritiquePinnedPrompt', 'Pinned prompt')}
          </PinnedFlag>
        )}
        <ThreadHeader>
          <ThreadToggle
            role="button"
            tabIndex={0}
            aria-expanded={open}
            onClick={() => setOpen(o => !o)}
            onKeyDown={e => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setOpen(o => !o);
              }
            }}
            data-testid="wall-thread-toggle"
          >
            <CritiqueAvatar
              id={post.createdBy?._id || ''}
              name={post.createdBy?.displayName}
            />
            <div style={{ flex: 1, minWidth: 0 }}>
              <TopicTitle data-testid="wall-topic-title">{heading}</TopicTitle>
              <TopicMeta>
                <span style={{ fontWeight: 700, color: '#6b675e' }}>
                  {userRefName(post.createdBy, t)}
                </span>
                <span data-testid="wall-thread-replycount">
                  · {replyCountLabel}
                </span>
                <span>
                  ·{' '}
                  {t('CritiqueLastActivity', 'active {{time}}', {
                    time: relativeTime(post.lastActivityAt || post.createdAt, t),
                  })}
                </span>
              </TopicMeta>
            </div>
            <Icon
              name={open ? 'chevron up' : 'chevron down'}
              style={{ color: 'var(--br-muted)' }}
              aria-hidden="true"
            />
          </ThreadToggle>
          {pinControl}
        </ThreadHeader>

        {open && (
          <>
            <PostBody style={{ borderTop: '1px solid #f4efe4', paddingTop: 12 }}>
              <PostText style={{ marginTop: 0 }}>{post.text}</PostText>
            </PostBody>
            {repliesSection}
          </>
        )}
      </PostCard>
    );
  }

  // ── Flat wall layout (unchanged): body always visible, replies behind a
  // toggle. ────────────────────────────────────────────────────────────────────
  return (
    <PostCard $pinned={post.pinned} data-testid={`wall-post-${post._id}`}>
      {post.pinned && (
        <PinnedFlag data-testid="wall-post-pinned">
          <Icon name="thumbtack" aria-hidden="true" />
          {t('CritiquePinnedPrompt', 'Pinned prompt')}
        </PinnedFlag>
      )}
      <PostBody>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <CritiqueAvatar
            id={post.createdBy?._id || ''}
            name={post.createdBy?.displayName}
          />
          <div style={{ flex: 1 }}>
            <span style={{ fontWeight: 700, fontSize: 13.5 }}>
              {userRefName(post.createdBy, t)}
            </span>{' '}
            <span style={{ fontSize: 11.5, color: '#a49f93' }}>
              · {relativeTime(post.createdAt, t)}
            </span>
          </div>
          {pinControl}
        </div>
        <PostText>{post.text}</PostText>
        <MetaRow>
          <MetaButton onClick={() => setOpen(o => !o)} data-testid="wall-replies-toggle">
            <Icon name="comment outline" aria-hidden="true" />
            {replyCountLabel}
          </MetaButton>
        </MetaRow>
      </PostBody>

      {open && repliesSection}
    </PostCard>
  );
};

type WallProps = {
  groupId: string;
  /** Host/moderator — may pin. Members may always post. */
  canPin: boolean;
  currentUser?: { _id: string; displayName?: string };
};

export const DiscussionWall: React.FC<WallProps> = ({
  groupId,
  canPin,
  currentUser,
}) => {
  const { t } = useTranslation();
  const topicsEnabled = useCritiqueTopicsEnabled();
  const [text, setText] = useState('');
  const [title, setTitle] = useState('');
  const [pinned, setPinned] = useState(false);
  const posts = useCritiquePosts(groupId);
  const createPost = useCreatePost(groupId);

  const submit = () => {
    const trimmed = text.trim();
    if (!trimmed) return;
    createPost.mutate(
      {
        text: trimmed,
        // Only send a topic when the flag is on; empty → omitted (plain post).
        title: topicsEnabled ? title.trim() || undefined : undefined,
        pinned: canPin ? pinned : undefined,
      },
      {
        onSuccess: () => {
          setText('');
          setTitle('');
          setPinned(false);
        },
      }
    );
  };

  if (posts.isLoading) return <CritiqueLoadingState />;
  if (posts.isError) {
    return (
      <CritiqueErrorState
        message={posts.error?.message}
        onRetry={() => posts.refetch()}
      />
    );
  }

  return (
    <div data-testid="discussion-wall">
      <Composer>
        <CritiqueAvatar
          id={currentUser?._id || ''}
          name={currentUser?.displayName}
          size={34}
        />
        <form style={{ flex: 1 }} onSubmit={event => { event.preventDefault(); submit(); }}>
          {topicsEnabled && (
            <TopicInput
              type="text"
              value={title}
              placeholder={t('CritiqueTopicOptional', 'Topic (optional)')}
              onChange={e => setTitle(e.target.value)}
              maxLength={TOPIC_MAX_LENGTH}
              aria-label={t('CritiqueTopicOptional', 'Topic (optional)')}
              data-testid="wall-topic-input"
            />
          )}
          <ComposerTextArea
            rows={2}
            value={text}
            placeholder={
              topicsEnabled
                ? t('CritiqueStartTopic', 'Start a topic or share a thought…')
                : t('CritiqueShareThought', 'Share a thought with the group…')
            }
            aria-label={topicsEnabled
              ? t('CritiqueStartTopic', 'Start a topic or share a thought…')
              : t('CritiqueShareThought', 'Share a thought with the group…')}
            onChange={event => setText(event.target.value)}
            data-testid="wall-post-input"
          />
          <ComposerActions data-testid="wall-composer-actions">
            {canPin ? (
              <label style={{ fontSize: 12.5, color: '#6b675e', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={pinned}
                  onChange={e => setPinned(e.target.checked)}
                  style={{ marginRight: 6 }}
                  data-testid="wall-pin-checkbox"
                />
                {t('CritiquePinThis', 'Pin as a discussion prompt')}
              </label>
            ) : (
              <span />
            )}
            <Button
              type="submit"
              size="small"
              aria-busy={createPost.isPending}
              disabled={!text.trim() || createPost.isPending}
              style={{ borderRadius: 8, fontWeight: 700 }}
              data-testid="wall-post-submit"
            >
              {t('Post', 'Post')}
            </Button>
          </ComposerActions>
        </form>
      </Composer>

      {(posts.data || []).length === 0 ? (
        <CritiqueEmptyState
          icon="comments outline"
          title={t('CritiqueStartConversation', 'Start the conversation')}
          body={
            canPin
              ? t(
                  'CritiqueEmptyWallHost',
                  'No posts yet. Pin a discussion prompt to get your writing family talking.'
                )
              : t('CritiqueEmptyWallMember', 'No posts yet. Be the first to say something.')
          }
        />
      ) : (
        (posts.data || []).map(p => (
          <PostThread
            key={p._id}
            groupId={groupId}
            post={p}
            canPin={canPin}
            topicsEnabled={topicsEnabled}
          />
        ))
      )}
    </div>
  );
};
